import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useListCrumb, usePageCrumbs } from "@/components/shell/breadcrumb";
import { Plus, Search, Trash2 } from "lucide-react";
import { PageBody } from "@/components/ui/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useCustomers, useSuppliers } from "@/api/hooks";
import { useErpCreate, useErpDelete, useErpList, useErpUpdate, type ErpRow } from "@/api/erp";
import { getLocalizedError } from "@/lib/api";
import { parseMoneyMajor } from "@/lib/moneyInput";
import { formatMoney } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";

type Tone = "neutral" | "info" | "warn" | "ok" | "danger" | "brand" | "brass";
export interface ErpField { key: string; labelKey: string; kind?: "text" | "number" | "money" | "date" | "select" | "customer" | "supplier"; options?: string[]; }
export interface ErpColumn { key: string; labelKey: string; kind?: "mono" | "money" | "date" | "badge"; tones?: Record<string, Tone>; align?: "left" | "right" | "center"; }
export interface ErpConfig {
  resource: string; titleKey: string; subtitleKey: string;
  columns: ErpColumn[]; fields: ErpField[]; soft?: boolean;
  statusField?: string; statusOptions?: string[]; statusTones?: Record<string, Tone>;
}

export function ErpResourcePage({ config }: { config: ErpConfig }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const list = useListQueryState();
  const [open, setOpen] = useState(false);
  const { data, isLoading, isFetching, error, refetch } = useErpList(config.resource, {
    q: list.q.trim() || undefined,
    page: list.page,
    pageSize: list.pageSize,
  });
  useClampListPage(data?.total, list.clampPage);
  const create = useErpCreate(config.resource);
  const del = useErpDelete(config.resource);
  const update = useErpUpdate(config.resource);
  const customers = useCustomers({});
  const suppliers = useSuppliers({});
  const canCreate = user ? can("records:create") : false;
  const canDelete = user ? can("records:delete") : false;
  const canUpdate = user ? can("records:update") : false;
  const rows = (data?.data ?? []) as ErpRow[];
  const [form, setForm] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; label: string } | null>(null);
  usePageCrumbs(open ? [{ label: t("common.new") }] : []);
  useListCrumb(useCallback(() => setOpen(false), []));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null);
    const body: ErpRow = {};
    for (const f of config.fields) {
      const v = form[f.key] ?? "";
      if (v === "" && f.kind !== "select") continue;
      if (f.kind === "number") body[f.key] = parseInt(v || "0", 10) || 0;
      else if (f.kind === "money") { const m = parseMoneyMajor(v); if (m.invalid) { setErr(t("common.invalidField", { field: t(f.labelKey) })); return; } body[f.key] = m.minor ?? 0; }
      else if (f.kind === "date") body[f.key] = v ? new Date(v + "T00:00:00Z").getTime() : undefined;
      else body[f.key] = v;
    }
    try { await create.mutateAsync(body); setOpen(false); setForm({}); }
    catch (e2) { setErr(getLocalizedError(e2, t, "erpResource.errorCreate")); }
  };

  const renderCell = (col: ErpColumn, row: ErpRow) => {
    const v = row[col.key];
    if (v === null || v === undefined || v === "") return <span className="text-ink-faint">—</span>;
    if (col.kind === "money") return <span className="font-mono tabular">{formatMoney(Number(v))}</span>;
    if (col.kind === "date") return <span className="font-mono tabular text-xs">{formatDate(Number(v))}</span>;
    if (col.kind === "badge") return <Badge tone={(col.tones?.[String(v)] ?? "neutral")}>{translateStatus(t, String(v))}</Badge>;
    if (col.kind === "mono") return <span className="font-mono tabular text-xs font-medium text-brand">{String(v)}</span>;
    return <span className="text-ink">{String(v)}</span>;
  };

  const actionCol = Boolean((canUpdate && config.statusField) || canDelete);
  const colCount = config.columns.length + (actionCol ? 1 : 0);

  return (
    <PageBody className={LIST_PAGE_BODY}>
      <ConfirmDialog
        open={!!pendingDelete}
        title={t("common.delete")}
        description={t("common.confirmArchive")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) });
        }}
      />
      {open && (
        <Modal title={t("common.new")} closeLabel={t("common.cancel")} onClose={() => setOpen(false)} className="max-w-3xl">
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {config.fields.map((f) => (
              <div key={f.key}>
                <FieldLabel>{t(f.labelKey)}</FieldLabel>
                {f.kind === "select" ? (
                  <Select
                    containerClassName="w-full"
                    value={form[f.key] ?? f.options?.[0] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  >
                    {(f.options ?? []).map((o) => <option key={o} value={o}>{translateStatus(t, o)}</option>)}
                  </Select>
                ) : f.kind === "customer" || f.kind === "supplier" ? (
                  <Select
                    containerClassName="w-full"
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  >
                    <option value="">—</option>
                    {(f.kind === "customer" ? customers.data?.data ?? [] : suppliers.data?.data ?? []).map((o) => (
                      <option key={o.id} value={o.id}>{o.companyName}</option>
                    ))}
                  </Select>
                ) : (
                  <Input type={f.kind === "date" ? "date" : "text"} inputMode={f.kind === "number" || f.kind === "money" ? "decimal" : undefined}
                    value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
              </div>
            ))}
            {err && <p className="sm:col-span-2 lg:col-span-3 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{err}</p>}
            <div className="flex gap-2 sm:col-span-2 lg:col-span-3">
              <Button type="submit" size="sm" disabled={create.isPending}>{create.isPending ? "…" : t("common.save")}</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            </div>
          </form>
        </Modal>
      )}
      <div className="flex shrink-0 items-center gap-2">
        <Input {...list.searchInput} leading={<Search />} placeholder={t("common.search")} className="w-full max-w-xs" />
        {canCreate && (
          <Button className="ml-auto" size="sm" onClick={() => setOpen((o) => !o)}><Plus className="size-4" /> {t("common.new")}</Button>
        )}
      </div>
      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("erpResource.errorLoad")}
        onRetry={() => refetch()}
        page={data?.page ?? list.page}
        pageSize={data?.pageSize ?? list.pageSize}
        total={data?.total ?? 0}
        onPageChange={list.updatePage}
        onPageSizeChange={list.updatePageSize}
      >
        <thead>
          <tr>
            {config.columns.map((c) => <Th key={c.key} align={c.align}>{t(c.labelKey)}</Th>)}
            {actionCol ? <Th align="right">{t("common.edit")}</Th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyTableRow colSpan={Math.max(1, colCount)}>{t("common.noMatches")}</EmptyTableRow>}
          {rows.map((row) => (
            <Tr key={String(row.id)}>
              {config.columns.map((c) => <Td key={c.key} align={c.align}>{renderCell(c, row)}</Td>)}
              {actionCol && (
                <Td align="right"><div className="flex items-center justify-end gap-1.5">
                  {canUpdate && config.statusField && config.statusOptions && (
                    <Select
                      containerClassName="h-7 w-28"
                      className="text-2xs"
                      value={String(row[config.statusField])}
                      onChange={(e) => update.mutate({ id: String(row.id), body: { [config.statusField!]: e.target.value } })}
                    >
                      {config.statusOptions.map((o) => <option key={o} value={o}>{translateStatus(t, o)}</option>)}
                    </Select>
                  )}
                  {canDelete && <button type="button" aria-label={t("common.delete")} onClick={() => setPendingDelete({ id: String(row.id), label: String(row.id) })} className="text-ink-faint hover:text-danger"><Trash2 className="size-3.5" /></button>}
                </div></Td>
              )}
            </Tr>
          ))}
        </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
